import React, { useCallback, useMemo, useRef, useState } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { Alert, Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select, Button, RadioCard } from "../../../../components/shared";
import { formatWholeNumberInput } from "../../../CommonComponents/FormattedInput/FormattedInput";
import WizardStepper from "../../../shared/WizardStepper/WizardStepper";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import DateRangeField from "../controls/DateRangeField";
import FreeSpinProviderSelect from "../controls/FreeSpinProviderSelect";
import FreeSpinGameSelect from "../controls/FreeSpinGameSelect";
import ClientCurrencyField from "../controls/ClientCurrencyField";
import {
  BannerFields,
  BannerPreview,
  type BannerPreviewVariant,
} from "../controls/BannerDesignField";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import { formatString } from "../../../../utils/constants/stringUtils";
import { filterByLabel } from "../../../../utils/selectFilter";
import { useDynamicBonusCatalog } from "../../../../queries/dynamicBonuses/useDynamicBonusCatalog";
import { useCreateCampaign } from "../../../../queries/dynamicBonuses/useCreateCampaign";
import { useAffiliatesList } from "../../../../queries/affiliates/useAffiliatesList";
import { useAffiliatePromoCodes } from "../../../../queries/affiliates/useAffiliatePromoCodes";
import {
  DEFAULT_TRANSACTION_TITLES,
  isDefaultTransactionTitle,
  RESULT_PARAM_SCHEMA,
  TRANSACTION_TITLE_PARAM_KEY,
} from "../constants";
import { paramFieldRules, stripInactiveConditionalResultParams, conditionalParamDependencyKeys } from "../resultParamRules";
import type { CampaignIn, ResultType } from "../types";
import { defaultDisplayLocations } from "./CreateCampaignWizard/displayLocationDefaults";
import wizardStyles from "../../../Affiliates/NewAffiliate/NewAffiliate.module.css";
import styles from "../stylesDynamicBonuses.module.css";

type AffiliateStep = "affiliate" | "reward" | "timing" | "review";

const getSteps = (): ReadonlyArray<{ id: AffiliateStep; label: string }> => [
  { id: "affiliate", label: AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_AFFILIATE_REFERRAL },
  { id: "reward", label: AppStrings.PAM_DYNAMIC_BONUS_WIZARD_STEP_REWARD },
  { id: "timing", label: AppStrings.PAM_DYNAMIC_BONUS_WIZARD_STEP_TIMING },
  { id: "review", label: AppStrings.PAM_DYNAMIC_BONUS_WIZARD_STEP_REVIEW },
];

type FormValues = {
  campaign_name: string;
  affiliate_id?: string;
  affiliate_referral_code: string;
  result_type_key: string;
  max_awards_per_user?: string;
  date_range?: [string | null, string | null];
  priority?: string;
  banner_title_key?: string;
  banner_msg_key?: string;
  transaction_title_key?: string;
  amount?: string;
  provider?: string;
  spin_count?: string;
  game_ids?: string;
  period_days?: string;
  /** ThreeOaks freebet dollars (requiredWhen provider=threeoaks). */
  value?: string;
  /** ThreeOaks freebet type select. */
  type?: string;
  bet_level?: string;
  currency?: string;
  total_bet?: string;
};

type Summary = {
  campaign_name?: string;
  affiliate_referral_code?: string;
  result_type_key?: string;
  max_awards_per_user?: string;
  starts_at?: string | null;
  ends_at?: string | null;
};

const getEmpty = (): string => AppStrings.PAM_DYNAMIC_BONUS_EMPTY_VALUE;

/** Fixed reward choices surfaced as bullets on the Reward step. */
const getAffiliateRewardOptions = (): {
  value: string;
  title: string;
  description: string;
}[] => [
  {
    value: "award_free_cash",
    title: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_CASH,
    description: AppStrings.PAM_DYNAMIC_BONUS_REWARD_FREE_CASH_DESCRIPTION,
  },
  {
    value: "award_free_spins",
    title: AppStrings.PAM_DYNAMIC_BONUS_RESULT_FREE_SPINS,
    description: AppStrings.PAM_DYNAMIC_BONUS_REWARD_FREE_SPINS_DESCRIPTION,
  },
];

const rewardLabel = (key?: string): string => {
  if (!key) return getEmpty();
  return getAffiliateRewardOptions().find((o) => o.value === key)?.title ?? key;
};

/** Collect non-empty result-param values for the selected reward type. */
const collectResultParams = (
  resultTypeKey: string,
  values: FormValues,
): Record<string, unknown> => {
  const params: Record<string, unknown> = {};
  for (const param of RESULT_PARAM_SCHEMA[resultTypeKey] ?? []) {
    if (param.percentOfTrigger || param.percentCompanion) continue;
    const value = values[param.key as keyof FormValues];
    if (value !== undefined && value !== "") {
      params[param.key] = value;
    }
  }
  // BONENG-960: an untouched transaction title is dropped so the backend
  // resolves its localized default — sending PAM's English literal would show
  // English in every other locale.
  if (
    isDefaultTransactionTitle(
      resultTypeKey,
      params[TRANSACTION_TITLE_PARAM_KEY],
    )
  ) {
    delete params[TRANSACTION_TITLE_PARAM_KEY];
  }
  return stripInactiveConditionalResultParams(resultTypeKey, params);
};

type RewardOptionsProps = {
  /** Injected by Form.Item — the current reward type_key. */
  value?: string;
  /** Injected by Form.Item — commits the chosen value to the form. */
  onChange?: (value: string) => void;
  /** Fired after the value changes (e.g. to prefill result defaults). */
  onAfterChange?: (value: string) => void;
};

/** Reward picker rendered as selectable bullets instead of a dropdown. */
const RewardOptions: React.FC<RewardOptionsProps> = ({
  value,
  onChange,
  onAfterChange,
}) => (
  <div role="radiogroup" className={styles.rewardOptions}>
    {getAffiliateRewardOptions().map((option) => (
      <RadioCard
        key={option.value}
        value={option.value}
        selected={value === option.value}
        onSelect={(next) => {
          onChange?.(next);
          onAfterChange?.(next);
        }}
        title={option.title}
        description={option.description}
      />
    ))}
  </div>
);

const CreateAffiliateCampaignPage: React.FC = () => {
  usePageTitle(AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_CAMPAIGN_TITLE);

  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [activeStep, setActiveStep] = useState<AffiliateStep>("affiliate");
  const [completedSteps, setCompletedSteps] = useState<Set<AffiliateStep>>(new Set());
  const [summary, setSummary] = useState<Summary>({});

  // Affiliate search
  const [affiliateSearch, setAffiliateSearch] = useState("");
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: affiliatesData, isFetching: affiliatesLoading } = useAffiliatesList({
    search: affiliateSearch,
    limit: 20,
    status: "active",
  });

  const { data: promoCodesData, isFetching: promoCodesLoading } = useAffiliatePromoCodes(
    selectedAffiliateId,
    { status: "active" },
  );

  const affiliateOptions = useMemo(
    () =>
      (affiliatesData?.items ?? []).map((a) => ({
        value: a.affiliate_id,
        label: a.company_name || a.contact_name || a.email || a.affiliate_id,
      })),
    [affiliatesData],
  );

  const promoCodeOptions = useMemo(
    () =>
      (promoCodesData?.items ?? [])
        .filter((c) => !c.disabled)
        .map((c) => ({
          value: c.promo_code,
          label: c.promo_name ? `${c.promo_code} — ${c.promo_name}` : c.promo_code,
        })),
    [promoCodesData],
  );

  const handleAffiliateSearch = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAffiliateSearch(value), 300);
  }, []);

  const handleAffiliateSelect = useCallback(
    (affiliateId: string) => {
      setSelectedAffiliateId(affiliateId);
      // Clear previously selected code when affiliate changes
      form.setFieldValue("affiliate_referral_code", undefined);
    },
    [form],
  );

  const watchedResultKey = Form.useWatch("result_type_key", form) as string | undefined;
  const watchedBannerTitle = Form.useWatch("banner_title_key", form) as string | undefined;
  const watchedBannerMessage = Form.useWatch("banner_msg_key", form) as string | undefined;
  const watchedProvider = Form.useWatch("provider", form) as string | undefined;

  const resultTypeKeyForCatalog = watchedResultKey ?? summary.result_type_key;

  const currentResultParams = useMemo(
    () => (resultTypeKeyForCatalog ? RESULT_PARAM_SCHEMA[resultTypeKeyForCatalog] ?? [] : []),
    [resultTypeKeyForCatalog],
  );

  // Banner copy gets a dedicated editor with a live promo preview; the rest of
  // the params render in the standard grid. Driven by the param schema (not a
  // hardcoded type_key list) so a reward only gets the banner editor when its
  // schema actually declares banner_title_key/banner_msg_key.
  const bannerParams = useMemo(() => {
    const titleParam = currentResultParams.find((p) => p.key === "banner_title_key");
    const msgParam = currentResultParams.find((p) => p.key === "banner_msg_key");
    const siblingParams: Record<string, unknown> = { provider: watchedProvider };
    const siblingMatches = (when?: { key: string; equals: string }) =>
      Boolean(when && String(siblingParams[when.key] ?? "") === when.equals);
    return {
      titleParam,
      msgParam,
      hasBanner: Boolean(titleParam && msgParam),
      gridParams: currentResultParams.filter((p) => {
        if (p.key === "banner_title_key" || p.key === "banner_msg_key") return false;
        // Affiliate campaigns fire on signup + referral code — there is no
        // triggering deposit/bet to take a percent of (BONENG-1171 / 913).
        if (p.percentOfTrigger || p.percentCompanion) return false;
        if (p.requiredWhen || p.visibleWhen) {
          return siblingMatches(p.requiredWhen) || siblingMatches(p.visibleWhen);
        }
        return true;
      }),
    };
  }, [currentResultParams, watchedProvider]);

  const showBanner = bannerParams.hasBanner;
  // Neither current getAffiliateRewardOptions() entry declares
  // banner_title_key/banner_msg_key, so showBanner (and therefore the
  // preview below) is always false today — this stays forward-wired for a
  // future free-spins-style reward option that does carry banner params,
  // rather than collapsing to a single hardcoded variant.
  const bannerVariant: BannerPreviewVariant =
    resultTypeKeyForCatalog === "award_free_spins" ? "freeSpins" : "cashDrops";

  const { data: catalog, isLoading: catalogLoading } = useDynamicBonusCatalog();
  const { mutate: createCampaign, isPending } = useCreateCampaign();

  const derivedResultType = useMemo((): ResultType | "" => {
    if (!catalog?.results?.length || !resultTypeKeyForCatalog) return "";
    return catalog.results.find((r) => r.type_key === resultTypeKeyForCatalog)?.result_type ?? "";
  }, [catalog, resultTypeKeyForCatalog]);

  // AWARD rewards reuse banner_title_key/banner_msg_key for push-notification
  // copy, not an in-app offer banner — only OFFER rewards get the visual mockup.
  const showBannerPreview = showBanner && derivedResultType === "offer";

  // True once a reward is picked, the catalog has finished loading, and the
  // catalog still doesn't recognize the type_key — e.g. it was renamed or
  // retired upstream. Surfaced explicitly instead of just disabling Submit.
  const catalogMismatch =
    Boolean(resultTypeKeyForCatalog) && !catalogLoading && !derivedResultType;

  /** Prefill any params that declare a default without overwriting user input. */
  const applyResultDefaults = useCallback(
    (typeKey: string) => {
      const schema = RESULT_PARAM_SCHEMA[typeKey] ?? [];
      schema.forEach((param) => {
        const defaultValue = param.default;
        // The title is refreshed unconditionally below, so a blank-only fill
        // here would be dead weight. Conditionally visible params wait until
        // the sibling provider matches.
        if (
          defaultValue === undefined ||
          param.key === TRANSACTION_TITLE_PARAM_KEY ||
          param.requiredWhen ||
          param.visibleWhen
        ) {
          return;
        }
        const name = param.key as keyof FormValues;
        const current = form.getFieldValue(name);
        if (current === undefined || current === "") {
          form.setFieldValue(name, defaultValue);
        }
      });
      // BONENG-960: the title names the reward, so it follows the reward type
      // instead of keeping the previous one's. Free Spins has no default, so
      // switching to it clears the value rather than submitting a Free Cash
      // title under a free-spins award.
      form.setFieldValue(
        TRANSACTION_TITLE_PARAM_KEY,
        DEFAULT_TRANSACTION_TITLES[typeKey],
      );
    },
    [form],
  );

  const markCompleted = useCallback((step: AffiliateStep) => {
    setCompletedSteps((prev) => {
      if (prev.has(step)) return prev;
      const next = new Set(prev);
      next.add(step);
      return next;
    });
  }, []);

  const handleStepClick = useCallback(
    (id: string) => {
      if (id === activeStep) return;
      if (!completedSteps.has(id as AffiliateStep)) return;
      setActiveStep(id as AffiliateStep);
    },
    [activeStep, completedSteps],
  );

  const advanceTo = useCallback(
    (fields: (keyof FormValues)[], current: AffiliateStep, next: AffiliateStep) => {
      form
        .validateFields(fields)
        .then(() => {
          const vals = form.getFieldsValue(fields) as Partial<FormValues>;
          setSummary((prev) => ({ ...prev, ...vals }));
          markCompleted(current);
          setActiveStep(next);
        })
        .catch(() => {});
    },
    [form, markCompleted],
  );

  const advanceFromRewardStep = useCallback(() => {
    const siblingParams = form.getFieldsValue(true) as Record<string, unknown>;
    const requiredParamKeys = (RESULT_PARAM_SCHEMA[watchedResultKey ?? ""] ?? [])
      .filter(
        (p) =>
          p.required ||
          (p.requiredWhen &&
            String(siblingParams[p.requiredWhen.key] ?? "") === p.requiredWhen.equals),
      )
      .map((p) => p.key as keyof FormValues);
    advanceTo(["result_type_key", ...requiredParamKeys], "reward", "timing");
  }, [advanceTo, form, watchedResultKey]);

  const onFinish = useCallback(
    (values: FormValues) => {
      const allValues = { ...form.getFieldsValue(true), ...values } as FormValues;
      const resolvedResultKey = allValues.result_type_key ?? summary.result_type_key ?? "";
      const resultType =
        catalog?.results?.find((r) => r.type_key === resolvedResultKey)?.result_type ?? "";
      if (!resultType) {
        message.error(AppStrings.PAM_DYNAMIC_BONUS_REWARD_CATALOG_ERROR);
        return;
      }

      const ttl = allValues.max_awards_per_user
        ? parseInt(allValues.max_awards_per_user, 10)
        : null;

      const params = collectResultParams(resolvedResultKey, allValues);
      const resolvedAffCode =
        allValues.affiliate_referral_code ?? summary.affiliate_referral_code ?? "";
      const resolvedName = allValues.campaign_name ?? summary.campaign_name ?? "";
      const resolvedStartsAt = allValues.date_range?.[0] ?? summary.starts_at ?? null;
      const resolvedEndsAt = allValues.date_range?.[1] ?? summary.ends_at ?? null;

      const payload: CampaignIn = {
        name: resolvedName,
        // Schedule is optional in the UI; the upstream requires a valid
        // datetime, so a blank start means "start now".
        starts_at: resolvedStartsAt ?? new Date().toISOString(),
        ends_at: resolvedEndsAt ?? null,
        priority: allValues.priority ? parseInt(allValues.priority, 10) : 0,
        affiliate_referral_code: resolvedAffCode || null,
        caps: ttl != null ? { max_awards_per_user: ttl } : undefined,
        // Duplicate awards rely on caps.max_awards_per_user (form default "1"),
        // not the retired not_already_awarded condition (BONENG-1031).
        eligibility_conditions: resolvedAffCode
          ? [
              {
                type: "campaign_affiliate_code_matches",
                params: { code: resolvedAffCode },
              },
            ]
          : [],
        steps: [
          {
            step_id: "step_1",
            trigger: { event_key: "user_signup" },
            conditions: [
              { type: "user_was_referred" },
              ...(resolvedAffCode
                ? [{ type: "referral_code_is_affiliate", params: { code: resolvedAffCode } }]
                : []),
            ],
            display_locations: defaultDisplayLocations({
              resultTypeKey: resolvedResultKey,
              triggerMode: "affiliate_referral",
            }),
            results: [
              {
                type: resolvedResultKey,
                result_type: resultType as ResultType,
                params,
              },
            ],
          },
        ],
        created_from_template_id: null,
      };

      createCampaign(payload, {
        onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS),
      });
    },
    [summary, catalog, createCampaign, navigate, form],
  );

  const s = summary;
  const canSubmitCampaign = Boolean(derivedResultType) && !catalogLoading;

  return (
    <>
      <FeatureFlagGuard
        feature="dynamicBonuses"
        innerFeatureFlags={["dynamicBonusesBetaMode"]}
        innerFlagsMode="all"
        combineMode="and"
        mode="route"
      >
        <div className={wizardStyles.page}>
          <div className={wizardStyles.header}>
            <button
              type="button"
              className={wizardStyles.backButton}
              onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)}
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_BACK_TO_CAMPAIGNS}
            >
              <span className={wizardStyles.backButtonIcon}>
                <LeftOutlined />
              </span>
            </button>
          </div>

          <div className={`${wizardStyles.content} ${styles.contentWideAside}`}>
            <div className={wizardStyles.wizardCard}>
              <WizardStepper
                steps={getSteps()}
                activeStepId={activeStep}
                completedStepIds={Array.from(completedSteps)}
                onStepClick={handleStepClick}
                idPrefix="create-affiliate-campaign"
                ariaLabel={AppStrings.PAM_DYNAMIC_BONUS_WIZARD_ARIA_AFFILIATE_CAMPAIGN}
              />

              <div className={wizardStyles.stepPanel}>
                <Form
                  form={form}
                  onFinish={onFinish}
                  preserve
                  initialValues={{ max_awards_per_user: "1" }}
                  className={styles.form}
                >

                  {/* ── Step 1: Affiliate ────────────────────────────────── */}
                  {activeStep === "affiliate" && (
                    <>
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_STEP_HINT}
                      </p>

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_NAME_LABEL}
                        name="campaign_name"
                        rules={[
                          {
                            required: true,
                            message: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_NAME_REQUIRED,
                          },
                        ]}
                      >
                        <Input
                          placeholder={
                            AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_CAMPAIGN_NAME_PLACEHOLDER
                          }
                        />
                      </Form.Item>

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_AFFILIATE_REFERRAL}
                        name="affiliate_id"
                        rules={[
                          {
                            required: true,
                            message: AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_SELECT_REQUIRED,
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          filterOption={false}
                          placeholder={
                            AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_SEARCH_PLACEHOLDER
                          }
                          options={affiliateOptions}
                          loading={affiliatesLoading}
                          notFoundContent={
                            affiliatesLoading ? (
                              <Spin size="small" />
                            ) : (
                              AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_NOT_FOUND
                            )
                          }
                          onSearch={handleAffiliateSearch}
                          onChange={handleAffiliateSelect}
                        />
                      </Form.Item>

                      <Form.Item
                        label={AppStrings.REFERRAL_CODE}
                        name="affiliate_referral_code"
                        rules={[
                          {
                            required: true,
                            message: AppStrings.PAM_DYNAMIC_BONUS_REFERRAL_CODE_REQUIRED,
                          },
                        ]}
                        tooltip={AppStrings.PAM_DYNAMIC_BONUS_REFERRAL_CODE_ACTIVE_TOOLTIP}
                      >
                        <Select
                          placeholder={
                            !selectedAffiliateId
                              ? AppStrings.PAM_DYNAMIC_BONUS_SELECT_AFFILIATE_FIRST
                              : promoCodesLoading
                                ? AppStrings.PAM_DYNAMIC_BONUS_LOADING_CODES
                                : promoCodeOptions.length === 0
                                  ? AppStrings.PAM_DYNAMIC_BONUS_NO_ACTIVE_CODES
                                  : AppStrings.PAM_DYNAMIC_BONUS_PICK_A_CODE
                          }
                          options={promoCodeOptions}
                          loading={promoCodesLoading}
                          disabled={!selectedAffiliateId || promoCodesLoading}
                          notFoundContent={
                            promoCodesLoading ? (
                              <Spin size="small" />
                            ) : (
                              AppStrings.PAM_DYNAMIC_BONUS_NO_ACTIVE_CODES
                            )
                          }
                          showSearch
                          filterOption={filterByLabel}
                        />
                      </Form.Item>

                      <div className={styles.stepFooter}>
                        <Button
                          intent="primary"
                          onClick={() =>
                            advanceTo(["campaign_name", "affiliate_id", "affiliate_referral_code"], "affiliate", "reward")
                          }
                        >
                          {AppStrings.CONTINUE}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ── Step 2: Reward ───────────────────────────────────── */}
                  {activeStep === "reward" && (
                    <>
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_REWARD_STEP_HINT}
                      </p>

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_REWARD_TYPE_LABEL}
                        name="result_type_key"
                        rules={[
                          {
                            required: true,
                            message: AppStrings.PAM_DYNAMIC_BONUS_REWARD_TYPE_REQUIRED,
                          },
                        ]}
                      >
                        <RewardOptions onAfterChange={applyResultDefaults} />
                      </Form.Item>

                      {bannerParams.gridParams.length > 0 && (
                        <div className={styles.resultParamsGrid}>
                          {bannerParams.gridParams.map((param) => (
                            <Form.Item
                              key={param.key}
                              label={param.label}
                              name={param.key}
                              tooltip={param.help}
                              rules={paramFieldRules(
                                param,
                                undefined,
                                undefined,
                                {
                                  provider: watchedProvider,
                                },
                              )}
                              dependencies={(() => {
                                const siblingKeys =
                                  conditionalParamDependencyKeys(param);
                                return siblingKeys.length > 0
                                  ? siblingKeys
                                  : undefined;
                              })()}
                            >
                              {param.type === "client-currency" ? (
                                <ClientCurrencyField provider={watchedProvider} />
                              ) : param.type === "free-spin-provider" ? (
                                <FreeSpinProviderSelect
                                  onChange={(val) => {
                                    form.setFieldValue("provider", val);
                                    form.setFieldValue("game_ids", "");
                                    form.setFieldValue("currency", undefined);
                                    form.setFieldValue("value", undefined);
                                    form.setFieldValue(
                                      "type",
                                      val === "threeoaks" ? "fixed" : undefined,
                                    );
                                  }}
                                />
                              ) : param.type === "free-spin-games" ? (
                                <FreeSpinGameSelect
                                  provider={watchedProvider}
                                />
                              ) : param.type === "select" ? (
                                <Select
                                  placeholder={formatString(
                                    AppStrings.PAM_DYNAMIC_BONUS_SELECT_FIELD,
                                    { title: param.label },
                                  )}
                                  options={param.options}
                                />
                              ) : (
                                <Input placeholder={param.placeholder} />
                              )}
                            </Form.Item>
                          ))}
                        </div>
                      )}

                      {showBanner && (
                        <BannerFields
                          titleName="banner_title_key"
                          messageName="banner_msg_key"
                          titleLabel={bannerParams.titleParam?.label}
                          messageLabel={bannerParams.msgParam?.label}
                          titleRequired={bannerParams.titleParam?.required}
                          messageRequired={bannerParams.msgParam?.required}
                          titlePlaceholder={bannerParams.titleParam?.placeholder}
                          messagePlaceholder={bannerParams.msgParam?.placeholder}
                        />
                      )}

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_PER_USER}
                        name="max_awards_per_user"
                        tooltip={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_TOOLTIP}
                        getValueFromEvent={(e) => formatWholeNumberInput(e.target.value)}
                      >
                        <Input
                          placeholder={AppStrings.PAM_DYNAMIC_BONUS_MAX_AWARDS_PLACEHOLDER}
                        />
                      </Form.Item>

                      <div className={styles.stepFooter}>
                        <Button intent="primary" onClick={advanceFromRewardStep}>
                          {AppStrings.CONTINUE}
                        </Button>
                        <Button intent="neutral" onClick={() => setActiveStep("affiliate")}>
                          {AppStrings.BACK}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ── Step 3: Timing ───────────────────────────────────── */}
                  {activeStep === "timing" && (
                    <>
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_TIMING_STEP_HINT}
                      </p>

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_PERIOD_LABEL}
                        name="date_range"
                      >
                        <DateRangeField />
                      </Form.Item>

                      <Form.Item
                        label={AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_LABEL}
                        name="priority"
                        getValueFromEvent={(e) => formatWholeNumberInput(e.target.value)}
                        tooltip={AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_PRIORITY_TOOLTIP}
                      >
                        <Input
                          placeholder={AppStrings.PAM_DYNAMIC_BONUS_PRIORITY_PLACEHOLDER}
                        />
                      </Form.Item>

                      <div className={styles.stepFooter}>
                        <Button
                          intent="primary"
                          onClick={() => {
                            const vals = form.getFieldsValue(["date_range", "priority"]) as Pick<FormValues, "date_range" | "priority">;
                            setSummary((prev) => ({
                              ...prev,
                              starts_at: vals.date_range?.[0] ?? null,
                              ends_at: vals.date_range?.[1] ?? null,
                            }));
                            markCompleted("timing");
                            setActiveStep("review");
                          }}
                        >
                          {AppStrings.CONTINUE}
                        </Button>
                        <Button intent="neutral" onClick={() => setActiveStep("reward")}>
                          {AppStrings.BACK}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* ── Step 4: Review + Submit ──────────────────────────── */}
                  {activeStep === "review" && (
                    <>
                      <p className={styles.stepHint}>
                        {AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_REVIEW_HINT}
                      </p>

                      <div className={styles.reviewGrid}>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_CAMPAIGN_NAME}
                          </span>
                          <span className={styles.reviewValue}>{s.campaign_name || getEmpty()}</span>
                        </div>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_AFFILIATE_CODE_LABEL}
                          </span>
                          <span className={styles.reviewValue}>{s.affiliate_referral_code || getEmpty()}</span>
                        </div>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_REWARD_TYPE}
                          </span>
                          <span className={styles.reviewValue}>{rewardLabel(s.result_type_key)}</span>
                        </div>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_MAX_AWARDS}
                          </span>
                          <span className={styles.reviewValue}>
                            {s.max_awards_per_user || AppStrings.PAM_DYNAMIC_BONUS_NO_CAP}
                          </span>
                        </div>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_STARTS_AT}
                          </span>
                          <span className={styles.reviewValue}>{s.starts_at || getEmpty()}</span>
                        </div>
                        <div className={styles.reviewItem}>
                          <span className={styles.reviewLabel}>
                            {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_ENDS_AT}
                          </span>
                          <span className={styles.reviewValue}>
                            {s.ends_at || AppStrings.PAM_DYNAMIC_BONUS_OPEN_ENDED}
                          </span>
                        </div>
                      </div>

                      <div className={styles.reviewNote}>
                        <strong>
                          {AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_WHAT_WILL_BE_CREATED_TITLE}
                        </strong>{" "}
                        {formatString(
                          AppStrings.PAM_DYNAMIC_BONUS_AFFILIATE_WHAT_WILL_BE_CREATED,
                          {
                            code: s.affiliate_referral_code || getEmpty(),
                            reward: rewardLabel(s.result_type_key),
                            maxAwards: s.max_awards_per_user
                              ? ` (${s.max_awards_per_user})`
                              : "",
                          },
                        )}
                      </div>

                      {catalogMismatch && (
                        <Alert
                          style={{ marginBottom: 20 }}
                          type="error"
                          showIcon
                          message={AppStrings.PAM_DYNAMIC_BONUS_REWARD_TYPE_UNAVAILABLE}
                          description={formatString(
                            AppStrings.PAM_DYNAMIC_BONUS_REWARD_CATALOG_MISMATCH,
                            { reward: rewardLabel(s.result_type_key) },
                          )}
                        />
                      )}

                      <div className={styles.stepFooter}>
                        <Button
                          intent="primary"
                          htmlType="submit"
                          loading={isPending}
                          disabled={!canSubmitCampaign}
                        >
                          {AppStrings.CREATE_CAMPAIGN}
                        </Button>
                        <Button intent="neutral" onClick={() => setActiveStep("timing")}>
                          {AppStrings.BACK}
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>

            {/* ── Sidebar summary ───────────────────────────────────────── */}
            <aside className={wizardStyles.summary}>
              <div className={wizardStyles.summarySection}>
                <p className={wizardStyles.summaryTitle}>
                  {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_STARTS_FALLBACK_NAME}
                </p>
                <dl className={wizardStyles.summaryList}>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>{AppStrings.NAME}</dt>
                    <dd className={wizardStyles.summaryValue}>{s.campaign_name || getEmpty()}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>
                      {AppStrings.PAM_DYNAMIC_BONUS_REVIEW_AFFILIATE_CODE_LABEL}
                    </dt>
                    <dd className={wizardStyles.summaryValue}>{s.affiliate_referral_code || getEmpty()}</dd>
                  </div>
                </dl>
              </div>
              <div className={wizardStyles.summarySection}>
                <p className={wizardStyles.summaryTitle}>
                  {AppStrings.PAM_DYNAMIC_BONUS_WIZARD_STEP_REWARD}
                </p>
                <dl className={wizardStyles.summaryList}>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>{AppStrings.TYPE}</dt>
                    <dd className={wizardStyles.summaryValue}>{rewardLabel(s.result_type_key)}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>
                      {AppStrings.PAM_DYNAMIC_BONUS_SUMMARY_MAX_PER_USER}
                    </dt>
                    <dd className={wizardStyles.summaryValue}>
                      {s.max_awards_per_user || AppStrings.PAM_DYNAMIC_BONUS_NO_CAP}
                    </dd>
                  </div>
                </dl>
              </div>
              {showBannerPreview && (
                <div className={wizardStyles.summarySection}>
                  <p className={wizardStyles.summaryTitle}>
                    {AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW}
                  </p>
                  <BannerPreview
                    title={watchedBannerTitle}
                    message={watchedBannerMessage}
                    variant={bannerVariant}
                  />
                </div>
              )}
              <div className={wizardStyles.summarySection}>
                <p className={wizardStyles.summaryTitle}>{AppStrings.PERIOD}</p>
                <dl className={wizardStyles.summaryList}>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>
                      {AppStrings.PAM_CAMPAIGNS_COLUMN_START}
                    </dt>
                    <dd className={wizardStyles.summaryValue}>{s.starts_at || getEmpty()}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>
                      {AppStrings.PAM_DYNAMIC_BONUS_SUMMARY_END}
                    </dt>
                    <dd className={wizardStyles.summaryValue}>
                      {s.ends_at || AppStrings.PAM_DYNAMIC_BONUS_OPEN_ENDED}
                    </dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default CreateAffiliateCampaignPage;
